import styled, { keyframes } from "styled-components";

/**
 * Product screens that track the camera through the freight journey.
 *
 * Each screen mirrors the beat the camera is parked on — customs at the port,
 * rate matching as the truck rolls out, receiving at the hub, tracking at the
 * doorstep — so the panel reads as "here's the part of the product that runs
 * this moment", not as decoration.
 *
 * Everything is drawn with CSS. No bitmaps, so it stays sharp at any DPR and
 * never needs re-exporting when the palette moves.
 */

const NAVY = "#0b1c75";
const INDIGO = "#4c5cf0";
const CYAN = "#3edcff";
const VIOLET = "#9d8cff";
const INK = "#5b6982";
const HAIRLINE = "#e4eaf6";
const CANVAS = "#f6f8fd";
const GOOD = "#12b07a";

const ROUTES = [
  "clearance/JKT-4471",
  "rates/JKT-4471",
  "receiving/DC-02",
  "tracking/JKT-4471"
];

interface Props {
  activeStep: number;
  /** Card width in px — the split presentation renders it larger. */
  width?: number;
  /** Height of the screen area. Must clear the tallest screen (rate matching). */
  height?: number;
}

const ScreenCard = ({ activeStep, width = 344, height = 300 }: Props) => (
  <Frame aria-hidden="true" style={{ width }}>
    <Chrome>
      <Dots>
        <Dot $color="#ff6b6b" />
        <Dot $color="#ffcf5c" />
        <Dot $color="#4bd07f" />
      </Dots>
      <UrlPill>
        <Lock />
        app.majura.ai/{ROUTES[activeStep] ?? ROUTES[0]}
      </UrlPill>
    </Chrome>

    <Viewport style={{ height }}>
      <Screen $active={activeStep === 0}>
        <ClearanceScreen />
      </Screen>
      <Screen $active={activeStep === 1}>
        <RatesScreen />
      </Screen>
      <Screen $active={activeStep === 2}>
        <ReceivingScreen />
      </Screen>
      <Screen $active={activeStep === 3}>
        <TrackingScreen />
      </Screen>
    </Viewport>
  </Frame>
);

/* ---------------------------------------------------------------- screens */

const DOCUMENTS = [
  { name: "Bill of Lading", state: "Validated" },
  { name: "Packing List", state: "Validated" },
  { name: "Commercial Invoice", state: "Validated" },
  { name: "PIB / CEISA 4.0", state: "Submitted" }
];

const ClearanceScreen = () => (
  <>
    <ScreenHead>
      <div>
        <Eyebrow>Customs clearance</Eyebrow>
        <Headline>MJRA-4471</Headline>
      </div>
      <Badge $tone="good">SPPB issued</Badge>
    </ScreenHead>

    <List>
      {DOCUMENTS.map((doc) => (
        <Row key={doc.name}>
          <Check />
          <RowLabel>{doc.name}</RowLabel>
          <RowMeta>{doc.state}</RowMeta>
        </Row>
      ))}
    </List>

    <Footnote>
      <Pulse />
      24 CEISA rules checked · 0 exceptions
    </Footnote>
  </>
);

const CARRIERS = [
  { name: "Trans Nusantara", price: "Rp 4.2M", days: "1 day", score: 96, best: true },
  { name: "Bahari Logistik", price: "Rp 4.6M", days: "1 day", score: 91, best: false },
  { name: "Sentra Cargo", price: "Rp 3.9M", days: "2 days", score: 78, best: false }
];

const RatesScreen = () => (
  <>
    <ScreenHead>
      <div>
        <Eyebrow>Rate matching</Eyebrow>
        <Headline>Tanjung Priok → DC-02</Headline>
      </div>
      <Badge $tone="info">3 quotes</Badge>
    </ScreenHead>

    <List>
      {CARRIERS.map((carrier) => (
        <QuoteRow key={carrier.name} $best={carrier.best}>
          <QuoteTop>
            <RowLabel>{carrier.name}</RowLabel>
            <Price>{carrier.price}</Price>
          </QuoteTop>
          <QuoteBottom>
            <RowMeta>{carrier.days} transit</RowMeta>
            <ScoreTrack>
              <ScoreFill style={{ width: `${carrier.score}%` }} $best={carrier.best} />
            </ScoreTrack>
            <Score>{carrier.score}</Score>
          </QuoteBottom>
        </QuoteRow>
      ))}
    </List>

    <Footnote>Scored on 18 months of on-time performance</Footnote>
  </>
);

const SKUS = [
  { sku: "SKU-8820", received: 480, expected: 480 },
  { sku: "SKU-8821", received: 300, expected: 300 },
  { sku: "SKU-8834", received: 176, expected: 220 }
];

const ReceivingScreen = () => (
  <>
    <ScreenHead>
      <div>
        <Eyebrow>Receiving · Dock 3</Eyebrow>
        <Headline>Inbound check-in</Headline>
      </div>
      <Badge $tone="info">Live</Badge>
    </ScreenHead>

    <List>
      {SKUS.map((line) => {
        const done = line.received >= line.expected;
        return (
          <Row key={line.sku} $stacked>
            <QuoteTop>
              <RowLabel>{line.sku}</RowLabel>
              <RowMeta>
                {line.received} / {line.expected}
              </RowMeta>
            </QuoteTop>
            <ScoreTrack>
              <ScoreFill
                style={{ width: `${(line.received / line.expected) * 100}%` }}
                $best={done}
              />
            </ScoreTrack>
          </Row>
        );
      })}
    </List>

    <Footnote>
      <Pulse />
      Counted at the dock — inventory already updated
    </Footnote>
  </>
);

const TIMELINE = [
  { label: "Discharged at port", time: "06:12", done: true },
  { label: "Departed terminal", time: "09:40", done: true },
  { label: "Received at DC-02", time: "14:05", done: true },
  { label: "Out for delivery", time: "16:20", done: false }
];

const TrackingScreen = () => (
  <>
    <ScreenHead>
      <div>
        <Eyebrow>Live tracking</Eyebrow>
        <Headline>Arriving 17:05</Headline>
      </div>
      <Badge $tone="good">On time</Badge>
    </ScreenHead>

    <Timeline>
      {TIMELINE.map((event, index) => (
        <Event key={event.label}>
          <Marker $done={event.done} $last={index === TIMELINE.length - 1} />
          <RowLabel>{event.label}</RowLabel>
          <RowMeta>{event.time}</RowMeta>
        </Event>
      ))}
    </Timeline>

    <Footnote>Shared with the customer — no status calls</Footnote>
  </>
);

/* ----------------------------------------------------------------- shell */

/* Positioning is the caller's job — the same card is used by all three
   presentations, which place it very differently. */
const Frame = styled.div`
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid ${HAIRLINE};
  box-shadow: 0 28px 64px rgba(11, 28, 117, 0.16);
  overflow: hidden;
  pointer-events: none;
`;

const Chrome = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: ${CANVAS};
  border-bottom: 1px solid ${HAIRLINE};
`;

const Dots = styled.div`
  display: flex;
  gap: 5px;
`;

const Dot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  opacity: 0.7;
`;

const UrlPill = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  padding: 4px 10px;
  border-radius: 999px;
  background: #ffffff;
  border: 1px solid ${HAIRLINE};
  font-size: 0.68rem;
  font-weight: 500;
  color: ${INK};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Lock = styled.span`
  flex-shrink: 0;
  width: 7px;
  height: 6px;
  border-radius: 1px;
  background: ${INK};
  box-shadow: 0 -3px 0 -1.5px transparent, inset 0 0 0 10px ${INK};
  position: relative;
  opacity: 0.6;

  &::before {
    content: "";
    position: absolute;
    left: 1.5px;
    top: -3px;
    width: 4px;
    height: 4px;
    border: 1px solid ${INK};
    border-bottom: none;
    border-radius: 2px 2px 0 0;
  }
`;

/* Fixed height so screens cross-fade in place instead of resizing the card.
   Defaults to the tallest screen (rate matching) so nothing clips. */
const Viewport = styled.div`
  position: relative;
`;

/* The outgoing screen clears out before the incoming one arrives — a plain
   symmetric cross-fade lets both sets of text read through each other. */
const Screen = styled.div<{ $active: boolean }>`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  visibility: ${({ $active }) => ($active ? "visible" : "hidden")};
  transform: translateY(${({ $active }) => ($active ? "0" : "10px")});
  transition: ${({ $active }) =>
    $active
      ? "opacity 0.3s ease-out 0.1s, transform 0.3s ease-out 0.1s, visibility 0s 0.1s"
      : "opacity 0.16s ease-in, transform 0.16s ease-in, visibility 0s 0.16s"};

  @media (prefers-reduced-motion: reduce) {
    transform: none;
    transition: opacity 0.15s linear, visibility 0s 0.15s;
  }
`;

/* ---------------------------------------------------------------- pieces */

const ScreenHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const Eyebrow = styled.div`
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: ${INK};
  opacity: 0.75;
`;

const Headline = styled.div`
  margin-top: 3px;
  font-size: 1rem;
  font-weight: 700;
  color: ${NAVY};
`;

const Badge = styled.span<{ $tone: "good" | "info" }>`
  flex-shrink: 0;
  padding: 4px 9px;
  border-radius: 999px;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: ${({ $tone }) => ($tone === "good" ? GOOD : INDIGO)};
  background: ${({ $tone }) =>
    $tone === "good" ? "rgba(18, 176, 122, 0.12)" : "rgba(76, 92, 240, 0.12)"};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
`;

const Row = styled.div<{ $stacked?: boolean }>`
  display: flex;
  flex-direction: ${({ $stacked }) => ($stacked ? "column" : "row")};
  align-items: ${({ $stacked }) => ($stacked ? "stretch" : "center")};
  gap: ${({ $stacked }) => ($stacked ? "6px" : "9px")};
  padding: 9px 11px;
  border-radius: 9px;
  background: ${CANVAS};
`;

const RowLabel = styled.span`
  flex: 1;
  font-size: 0.78rem;
  font-weight: 600;
  color: ${NAVY};
`;

const RowMeta = styled.span`
  font-size: 0.7rem;
  font-weight: 600;
  color: ${INK};
  font-variant-numeric: tabular-nums;
`;

const Check = styled.span`
  flex-shrink: 0;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background: rgba(18, 176, 122, 0.14);
  position: relative;

  &::after {
    content: "";
    position: absolute;
    left: 5px;
    top: 3px;
    width: 3px;
    height: 6px;
    border: solid ${GOOD};
    border-width: 0 1.8px 1.8px 0;
    transform: rotate(45deg);
  }
`;

const QuoteRow = styled.div<{ $best: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 9px 11px;
  border-radius: 9px;
  background: ${({ $best }) => ($best ? "rgba(76, 92, 240, 0.07)" : CANVAS)};
  border: 1px solid ${({ $best }) => ($best ? "rgba(76, 92, 240, 0.28)" : "transparent")};
`;

const QuoteTop = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
`;

const QuoteBottom = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
`;

const Price = styled.span`
  font-size: 0.78rem;
  font-weight: 700;
  color: ${NAVY};
  font-variant-numeric: tabular-nums;
`;

const ScoreTrack = styled.div`
  flex: 1;
  height: 4px;
  border-radius: 999px;
  background: rgba(11, 28, 117, 0.09);
  overflow: hidden;
`;

const ScoreFill = styled.div<{ $best: boolean }>`
  height: 100%;
  border-radius: 999px;
  background: ${({ $best }) =>
    $best ? `linear-gradient(90deg, ${CYAN}, ${INDIGO})` : "rgba(11, 28, 117, 0.24)"};
  transition: width 0.5s ease;
`;

const Score = styled.span`
  width: 18px;
  text-align: right;
  font-size: 0.68rem;
  font-weight: 700;
  color: ${INK};
  font-variant-numeric: tabular-nums;
`;

const Timeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Event = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 7px 2px;
`;

const Marker = styled.span<{ $done: boolean; $last: boolean }>`
  position: relative;
  flex-shrink: 0;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${({ $done }) => ($done ? INDIGO : VIOLET)};
  box-shadow: 0 0 0 3px ${({ $done }) => ($done ? "rgba(76, 92, 240, 0.16)" : "rgba(157, 140, 255, 0.2)")};

  /* connector down to the next event */
  &::after {
    content: "";
    display: ${({ $last }) => ($last ? "none" : "block")};
    position: absolute;
    left: 50%;
    top: 12px;
    width: 1.5px;
    height: 22px;
    transform: translateX(-50%);
    background: rgba(11, 28, 117, 0.14);
  }
`;

const blink = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.35; transform: scale(0.8); }
`;

const Pulse = styled.span`
  flex-shrink: 0;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${GOOD};
  animation: ${blink} 1.8s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Footnote = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  margin-top: auto;
  padding-top: 10px;
  border-top: 1px solid ${HAIRLINE};
  font-size: 0.68rem;
  font-weight: 500;
  color: ${INK};
`;

export default ScreenCard;
